import React, { CSSProperties, useEffect, useState } from 'react';
import { useAlert } from 'libs/react-alert';
import { Trans } from '@lingui/macro';

import { sessionStore, TFolderCodes } from '../../services';
import { QR } from './qr.component';
import './qr.component.scss';

type TQrPreviewProps = {};

export const QrPreviewComponent: React.FunctionComponent<TQrPreviewProps> = () => {
	const [codes, setCodes] = useState<string[]>([]);
	const [activeCode, setActiveCode] = useState<string | undefined>(undefined);
	const [isLoading, toggleLoading] = useState<boolean>(true);
	const [isTitleHidden, setTitleHidden] = useState<boolean>(sessionStore.hideTitle$.value);
	const alert = useAlert();

	useEffect(() => {
		const subscription = sessionStore.selectedCodes$.subscribe((_codes: TFolderCodes) => {
			toggleLoading(true);
			const codes: string[] = [];
			Object.values(_codes).forEach(c => {
				codes.push(...c);
			});
			setTimeout(() => {
				setCodes(codes);
				toggleLoading(false);
			}, 1500);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		const subscription = sessionStore.selectedCode$.subscribe((code: string | undefined) => {
			if (code) {
				setActiveCode(code);
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		sessionStore.hideTitle$.next(isTitleHidden);
	}, [isTitleHidden]);

	const printBack = () => {
		toggleLoading(true);
		const url = '/api/pdf';

		fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				codes: codes,
				hideTitle: isTitleHidden,
			}),
		})
			.then(res => {
				if (res.status > 200) {
					throw new Error('Something went wrong');
				}
				return res.blob();
			})
			.then(blob => {
				const link = document.createElement('a');
				link.download = 'qr.pdf';
				link.href = URL.createObjectURL(blob);
				link.click();
				toggleLoading(false);
			})
			.catch(() => {
				toggleLoading(false);
				alert.error(<Trans>PDF generation error. Please try again later.</Trans>);
			});
	};

	return codes.length ? (
		<div className="preview-container">
			{isLoading && (
				<div style={loadingStyle}>
					<div style={loadingTextStyle}>
						<div className="loadingio-spinner-dual-ball-5sbz49hiy5l">
							<div className="ldio-zv8kdttdcuc">
								<div />
								<div />
								<div />
							</div>
						</div>
						<h3>
							<Trans>Loading ...</Trans>
						</h3>
					</div>
				</div>
			)}

			<div id="notForPrint" className="qr">
				{activeCode && (
					<QR
						afterRender={() => {}}
						code={activeCode}
						key={'preview'}
						forPrint={false}
						hiddenTitle={isTitleHidden}
						id="preview-example"
					/>
				)}
			</div>
			<div className="button-block">
				<label htmlFor="hideTitle" className="button-block-label">
					<input
						checked={isTitleHidden}
						onChange={() => setTitleHidden(!isTitleHidden)}
						id="hideTitle"
						type="checkbox"
					/>
					<span>
						<Trans>Hide title</Trans>
					</span>
				</label>
				<button className="print-button" disabled={!codes.length} onClick={() => printBack()}>
					<Trans>Print</Trans>
				</button>
			</div>
		</div>
	) : null;
};

const loadingStyle = {
	position: 'fixed',
	width: '100%',
	height: '100%',
	top: 0,
	zIndex: 9999,
	left: 0,
	backgroundColor: 'rgba(255,255,255,0.9)',
} as CSSProperties;
const loadingTextStyle = {
	position: 'absolute',
	width: '100%',
	top: '50%',
	transform: 'translateY(-50%)',
	textAlign: 'center',
	color: '#000',
} as CSSProperties;
